import { pxToEm } from 'util/pxToEm';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  IconButton,
  Typography,
  Stack,
  Switch as MuiSwitch,
  useTheme,
} from '@mui/material';
import { ExpandMore, ExpandLess, Check } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useEventsApi, useSchoolsApi } from 'services/api';
import useSWR from 'swr';
import { Button } from 'components/ui';
import { useProfile } from 'hooks';
import {
  EventSetupCategory,
  UpdateEventSetupCategoriesRequest,
} from 'types/event-setup';
import {
  colorNavyBlue,
  colorLightBlue,
  colorYellowHover,
  colorDisabledBg,
  colorDisabledText,
} from 'customColors';
import { ApprovalSequence } from './approval-sequence';
import { CalendarSettings } from './calendar-settings';
import { EventSetupTabs } from './event-setup-tabs';

export interface EventSetupTabProps {
  schoolId: string;
}

type TabValue = 'setup' | 'calendar-settings' | 'approval';

export const EventSetupTab = ({
  schoolId,
}: EventSetupTabProps): ReactElement => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState<TabValue>('setup');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(),
  );
  const [isLoading, setIsLoading] = useState(false);
  const [categories, setCategories] = useState<EventSetupCategory[]>([]);
  const [originalData, setOriginalData] = useState<EventSetupCategory[]>([]);

  const { profile } = useProfile();

  const eventsApi = useEventsApi();
  const schoolsApi = useSchoolsApi();
  const { enqueueSnackbar } = useSnackbar();

  const { data: eventSetupData, mutate } = useSWR(
    schoolId ? `/schools/${schoolId}/event-setup-categories` : null,
    async () => {
      if (!schoolId) return null;
      const result = await eventsApi.getEventSetupCategories(schoolId);
      if (result.success) return result.value;
      throw new Error(result.error.message);
    },
  );

  const { data: schoolData, mutate: mutateSchoolData } = useSWR(
    schoolId ? `/schools/${schoolId}` : null,
    async () => {
      if (!schoolId) return null;
      const result = await schoolsApi.getSchool(schoolId);
      if (result.success) return result.value;
      throw new Error(result.error.message);
    },
  );

  useEffect(() => {
    if (eventSetupData) {
      setCategories(eventSetupData.categories);
      setOriginalData(eventSetupData.categories);
      if (eventSetupData.categories.length > 0) {
        setExpandedCategories(
          new Set([eventSetupData.categories[0].eventSetupCategoryId]),
        );
      }
    }
  }, [eventSetupData]);


  const hasChanges = useMemo(() => {
    if (originalData.length === 0 || categories.length === 0) return false;
    return JSON.stringify(categories) !== JSON.stringify(originalData);
  }, [categories, originalData]);


  const toggleCategoryExpansion = (categoryId: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) newSet.delete(categoryId);
      else newSet.add(categoryId);
      return newSet;
    });
  };

  const handleCategoryToggle = (categoryId: string, isEnabled: boolean) => {
    setCategories(prev =>
      prev.map(cat =>
        cat.eventSetupCategoryId === categoryId
          ? {
              ...cat,
              isEnabled,
              questions: cat.questions.map(q => ({ ...q, isEnabled })),
            }
          : cat,
      ),
    );
  };

  const handleCategoryApprovalToggle = (
    categoryId: string,
    isApprovalMandatory: boolean,
  ) => {
    setCategories(prev =>
      prev.map(cat =>
        cat.eventSetupCategoryId === categoryId
          ? { ...cat, isApprovalMandatory }
          : cat,
      ),
    );
  };

  const handleQuestionToggle = (
    categoryId: string,
    questionId: string,
    isEnabled: boolean,
  ) => {
    setCategories(prev =>
      prev.map(cat =>
        cat.eventSetupCategoryId === categoryId
          ? {
              ...cat,
              questions: cat.questions.map(q =>
                q.eventSetupCategoryQuestionId === questionId
                  ? { ...q, isEnabled }
                  : q,
              ),
            }
          : cat,
      ),
    );
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const requestData: UpdateEventSetupCategoriesRequest = {
        categories: categories.map(cat => ({
          eventSetupCategoryId: cat.eventSetupCategoryId,
          isApprovalMandatory: cat.isApprovalMandatory,
          isEnabled: cat.isEnabled,
          questions: cat.questions.map(q => ({
            eventSetupCategoryQuestionId: q.eventSetupCategoryQuestionId,
            isRequired: q.isRequired,
            isEnabled: q.isEnabled,
          })),
        })),
      };

      const result = await eventsApi.updateEventSetupCategories(
        requestData,
        schoolId,
      );

      if (result.success) {
        enqueueSnackbar('Event setup configuration updated successfully', {
          variant: 'success',
        });
        await mutate();
      } else {
        enqueueSnackbar(result.error.message, { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar('Failed to update event setup configuration', {
        variant: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCalendarSettingsUpdate = async () => {
    await mutateSchoolData();
  };

  if (!eventSetupData) {
    return (
      <Box mt={20} textAlign="center">
        <Typography>Loading event setup configuration...</Typography>
      </Box>
    );
  }

  return (
    <Box mt={{ xs: 16, sm: 20 }}>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 500,
          mb: { xs: 8, sm: 16 },
          fontSize: { xs: pxToEm(20), sm: pxToEm(24) },
        }}
      >
        Event Settings
      </Typography>

      <EventSetupTabs activeTab={activeTab} onTabChange={setActiveTab} />

      {activeTab === 'calendar-settings' && (
        <CalendarSettings
          schoolId={schoolId}
          minimumEventSubmissionDays={schoolData?.minimumEventSubmissionDays ?? 0}
          onUpdate={handleCalendarSettingsUpdate}
        />
      )}

      {activeTab === 'setup' && (
        <>
          <Box mb={16}>
            <Typography
              fontSize={{ xs: pxToEm(13), sm: pxToEm(14) }}
              fontWeight={400}
              color={theme.palette.text.primary}
            >
              Enabled categories will appear on the event request form.
            </Typography>
          </Box>

          <Stack spacing={16}>
            {categories.map(category => (
              <Card
                key={category.eventSetupCategoryId}
                sx={{
                  border: `1px solid ${theme.palette.neutral.dark}`,
                  bgcolor: '#fff',
                  borderRadius: '8px',
                  boxShadow: 'none',
                  p: '16px',
                }}
              >
                <Box
                  display="flex"
                  flexDirection={{ xs: 'column', sm: 'row' }}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  justifyContent="space-between"
                  gap={{ xs: 16, sm: 0 }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    width={{ xs: '100%', sm: 'auto' }}
                    sx={{ flex: { xs: 'none', sm: 1 } }}
                  >
                    <Typography
                      fontWeight={500}
                      color="#000"
                      fontSize={{ xs: pxToEm(15), sm: pxToEm(16) }}
                      onClick={() =>
                        toggleCategoryExpansion(category.eventSetupCategoryId)
                      }
                      sx={{
                        cursor: 'pointer',
                        userSelect: 'none',
                      }}
                    >
                      {category.name}
                    </Typography>
                    <IconButton
                      onClick={() =>
                        toggleCategoryExpansion(category.eventSetupCategoryId)
                      }
                      size="small"
                      sx={{
                        display: { xs: 'flex', sm: 'none' },
                        p: 0.5,
                        ml: 8,
                      }}
                    >
                      {expandedCategories.has(category.eventSetupCategoryId) ? (
                        <ExpandLess sx={{ fontSize: 24 }} />
                      ) : (
                        <ExpandMore sx={{ fontSize: 24 }} />
                      )}
                    </IconButton>
                  </Box>

                  <Box
                    display="flex"
                    flexDirection={{ xs: 'column', sm: 'row' }}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    gap={{ xs: 24, sm: 24 }}
                    width={{ xs: '100%', sm: 'auto' }}
                  >
                    <Box display="flex" alignItems="center" gap={8}>
                      <MuiSwitch
                        checked={category.isEnabled}
                        onChange={e =>
                          handleCategoryToggle(
                            category.eventSetupCategoryId,
                            e.target.checked,
                          )
                        }
                        icon={
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              borderRadius: '50%',
                              bgcolor: colorNavyBlue,
                              border: `2px solid ${colorNavyBlue}`,
                            }}
                          />
                        }
                        checkedIcon={
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              bgcolor: '#fff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Check
                              sx={{ fontSize: 16, color: colorNavyBlue }}
                            />
                          </Box>
                        }
                        sx={{
                          width: 52,
                          height: 32,
                          padding: 0,
                          '& .MuiSwitch-switchBase': {
                            padding: 0,
                            margin: '8px 4px',
                            transitionDuration: '300ms',
                            '&.Mui-checked': {
                              margin: '4px',
                              transform: 'translateX(20px)',
                              color: '#fff',
                              '& + .MuiSwitch-track': {
                                backgroundColor: colorNavyBlue,
                                opacity: 1,
                                border: 0,
                              },
                            },
                          },
                          '& .MuiSwitch-track': {
                            borderRadius: 35,
                            backgroundColor: colorLightBlue,
                            opacity: 1,
                            border: `2px solid ${colorNavyBlue}`,
                          },
                        }}
                      />
                      <Typography fontSize={pxToEm(14)} color="#000">
                        Enable Category
                      </Typography>
                    </Box>

                    <Box display="flex" alignItems="center" gap={8}>
                      <MuiSwitch
                        checked={category.isApprovalMandatory}
                        onChange={e =>
                          handleCategoryApprovalToggle(
                            category.eventSetupCategoryId,
                            e.target.checked,
                          )
                        }
                        icon={
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              borderRadius: '50%',
                              bgcolor: colorNavyBlue,
                              border: `2px solid ${colorNavyBlue}`,
                            }}
                          />
                        }
                        checkedIcon={
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              bgcolor: '#fff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Check
                              sx={{ fontSize: 16, color: colorNavyBlue }}
                            />
                          </Box>
                        }
                        sx={{
                          width: 52,
                          height: 32,
                          padding: 0,
                          '& .MuiSwitch-switchBase': {
                            padding: 0,
                            margin: '8px 4px',
                            transitionDuration: '300ms',
                            '&.Mui-checked': {
                              margin: '4px',
                              transform: 'translateX(20px)',
                              color: '#fff',
                              '& + .MuiSwitch-track': {
                                backgroundColor: colorNavyBlue,
                                opacity: 1,
                                border: 0,
                              },
                            },
                          },
                          '& .MuiSwitch-track': {
                            borderRadius: 35,
                            backgroundColor: colorLightBlue,
                            opacity: 1,
                            border: `2px solid ${colorNavyBlue}`,
                          },
                        }}
                      />
                      <Typography fontSize={pxToEm(14)} color="#000">
                        Require Approval
                      </Typography>
                    </Box>

                    <IconButton
                      onClick={() =>
                        toggleCategoryExpansion(category.eventSetupCategoryId)
                      }
                      size="small"
                      sx={{
                        display: { xs: 'none', sm: 'flex' },
                        ml: 8,
                        p: 0.5,
                      }}
                    >
                      {expandedCategories.has(category.eventSetupCategoryId) ? (
                        <ExpandLess sx={{ fontSize: 24 }} />
                      ) : (
                        <ExpandMore sx={{ fontSize: 24 }} />
                      )}
                    </IconButton>
                  </Box>
                </Box>

                {expandedCategories.has(category.eventSetupCategoryId) && (
                  <Box
                    mt={{ xs: 16, sm: 20 }}
                    pt={{ xs: 16, sm: 0 }}
                    borderTop={{
                      xs: `1px solid ${theme.palette.neutral.dark}`,
                      sm: 'none',
                    }}
                  >
                    <Stack spacing={{ xs: 12, sm: 16 }} sx={{ gap: '16px' }}>
                      {category.questions.map(question => (
                        <Box
                          key={question.eventSetupCategoryQuestionId}
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Typography
                            fontWeight={400}
                            fontSize={{ xs: pxToEm(14), sm: pxToEm(15) }}
                            color="#000"
                            sx={{ flex: 1 }}
                          >
                            {question.name}
                          </Typography>

                          <MuiSwitch
                            checked={question.isEnabled}
                            onChange={e =>
                              handleQuestionToggle(
                                category.eventSetupCategoryId,
                                question.eventSetupCategoryQuestionId,
                                e.target.checked,
                              )
                            }
                            icon={
                              <Box
                                sx={{
                                  width: 16,
                                  height: 16,
                                  borderRadius: '50%',
                                  bgcolor: colorNavyBlue,
                                  border: `2px solid ${colorNavyBlue}`,
                                }}
                              />
                            }
                            checkedIcon={
                              <Box
                                sx={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: '50%',
                                  bgcolor: '#fff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <Check
                                  sx={{ fontSize: 16, color: colorNavyBlue }}
                                />
                              </Box>
                            }
                            sx={{
                              width: 52,
                              height: 32,
                              padding: 0,
                              '& .MuiSwitch-switchBase': {
                                padding: 0,
                                margin: '8px 4px',
                                transitionDuration: '300ms',
                                '&.Mui-checked': {
                                  margin: '4px',
                                  transform: 'translateX(20px)',
                                  color: '#fff',
                                  '& + .MuiSwitch-track': {
                                    backgroundColor: colorNavyBlue,
                                    opacity: 1,
                                    border: 0,
                                  },
                                },
                              },
                              '& .MuiSwitch-track': {
                                borderRadius: 35,
                                backgroundColor: colorLightBlue,
                                opacity: 1,
                                border: `2px solid ${colorNavyBlue}`,
                              },
                            }}
                          />
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Card>
            ))}
          </Stack>

          <Box
            mt={32}
            display="flex"
            justifyContent={{ xs: 'flex-start', sm: 'flex-start' }}
          >
            <Button
              variant="vibrant"
              onClick={handleSave}
              disabled={isLoading || !hasChanges}
              sx={{
                display: 'flex',
                width: '300px',
                height: '47px',
                minHeight: '47px',
                maxHeight: '47px',
                padding: '16px',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '8px',
                bgcolor: theme.palette.vibrant.primary,
                color: '#000',
                fontWeight: 500,
                fontSize: pxToEm(14),
                textTransform: 'none',
                borderRadius: '6px',
                '&:hover': {
                  bgcolor: colorYellowHover,
                },
                '&:disabled': {
                  bgcolor: colorDisabledBg,
                  color: colorDisabledText,
                },
              }}
            >
              {isLoading ? 'Saving...' : 'Save Setup & Services'}
            </Button>
          </Box>
        </>
      )}

      {activeTab === 'approval' &&
        profile?.school?.isSequentialApprovalEnabled && (
          <ApprovalSequence schoolId={schoolId} approvalFlowOrder={schoolData?.approvalFlowOrder} />
        )}
    </Box>
  );
};
